import { t } from '@lingui/macro';
import React, { useState, useCallback } from 'react';

import { type IdentityResponse } from '@api/v4/private/people_tagging/faces';
import { SuggestedNamesList } from '@components/people_tags/common/SuggestedNamesList';

import { Person } from '@components/people_tags/customPeopleTagTypes';

import {
  PersonTag,
  PersonImage,
  PersonName,
  PersonNameText,
  RemovePersonButton,
  CustomDropdown,
  DropdownHeader,
  DropdownIcon,
  InputStyled,
  FaceThumbnailImage
} from './styles/PeopleInAssetsComponents';

// Face data structure used internally
export interface FaceData {
  identityId?: string;
  faceId?: string;
  name: string;
  assetUrl: string;
  faceThumbnailUrl: string;
  isHidden: boolean;
}

interface PeopleTagEditFieldProps {
  face: FaceData;
  identities: IdentityResponse[];
  onRemoveFaceName: (face: FaceData) => void;
  onMergeIdentities: (sourceIdentityId: string, destinationIdentity: Person) => void;
  onUpdateIdentityName: (identityId: string, name: string) => void;
}

export const PeopleTagEditField: React.FC<PeopleTagEditFieldProps> = ({
  face,
  identities,
  onRemoveFaceName,
  onMergeIdentities,
  onUpdateIdentityName,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchInput, setSearchInput] = useState('');

  const hasName = face.name && face.name.trim() !== '';

  // Map identities to suggestions format
  const getFilteredSuggestions = useCallback((name: string) => {
    if (!name || name.trim() === '') {
      return [];
    }

    const filteredIdentities = identities.filter(
      (identity) => identity.name?.trim() && identity.name?.toLowerCase().includes(name.toLowerCase())
    );

    return filteredIdentities.map((identity) => ({
      face_uri: identity.identity_thumbnail_url || '',
      person_id: identity.id,
      tag_name: identity.name,
      tag_count: identity.tag_count
    }));
  }, [identities]);

  const handleSearchInputChange = useCallback((value: string): void => {
    setSearchInput(value);
    if (value.trim() !== '') {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  }, []);

  const handleMergeIdentities = useCallback((selectedIdentity: Person): void => {
    onMergeIdentities(face.identityId, selectedIdentity); // person_id is the destination identity id
    setIsOpen(false);
    setSearchInput('');
  }, [face.identityId, onMergeIdentities]);

  const handleUpdateIdentityName = useCallback((name: string): void => {
    onUpdateIdentityName(face.identityId || '', name);
    setIsOpen(false);
    setSearchInput('');
  }, [face.identityId, onUpdateIdentityName]);

  const toggleDropdown = useCallback((): void => {
    setIsOpen((prev) => !prev);
  }, []);

  if (hasName) {
    return (
      <PersonTag>
        <PersonImage>
          <FaceThumbnailImage alt={face.name} src={face.faceThumbnailUrl} />
        </PersonImage>
        <PersonName>
          <PersonNameText>{face.name}</PersonNameText>
          <RemovePersonButton
            aria-label={t`Remove ${face.name}`}
            onClick={(): void => onRemoveFaceName(face)}
          >
            <span className="icon bff-close" />
          </RemovePersonButton>
        </PersonName>
      </PersonTag>
    );
  }

  return (
    <PersonTag>
      <PersonImage>
        <FaceThumbnailImage alt={t`Unidentified person`} src={face.faceThumbnailUrl} />
      </PersonImage>
      <PersonName>
        <CustomDropdown>
          <DropdownHeader>
            <InputStyled
              onChange={(e): void => handleSearchInputChange(e.target.value)}
              placeholder={t`Name person`}
              type="text"
              value={searchInput}
            />
            <DropdownIcon onClick={toggleDropdown}>
              <span className="icon bff-caret-down" />
            </DropdownIcon>
          </DropdownHeader>

          {isOpen && (
            <SuggestedNamesList
              handleCreateNewPerson={handleUpdateIdentityName}
              handleOnSelectTargetPerson={handleMergeIdentities}
              showCreateOption
              suggestions={getFilteredSuggestions(searchInput)}
              typedName={searchInput}
            />
          )}
        </CustomDropdown>
      </PersonName>
    </PersonTag>
  );
};
